import { useHydrated } from '@src/utils/useHydrated';
import { useTranslation } from 'react-i18next';
import { Button } from '@src/components/Button/Button';
import { Cluster } from '@src/components/Cluster/Cluster';
import { RuFlagIcon } from '@src/components/icons/RuFlagIcon';
import { UsFlagIcon } from '@src/components/icons/UsFlagIcon';
import { Lang } from '@src/i18n/lang';
import { isDotCom, setSiteLanguage } from '@src/site';

export interface LocaleToggleProps {
  testId?: string;
}

/** RU ↔ EN flags. Only on .ru — .com stays English and never mentions the other site. */
export function LocaleToggle({ testId = 'footer-locale' }: LocaleToggleProps) {
  const hydrated = useHydrated();
  const { i18n, t } = useTranslation();

  if (isDotCom()) {
    return null;
  }

  const current = i18n.language === Lang.EN ? Lang.EN : Lang.RU;

  function select(lang: Lang) {
    if (lang === current) {
      return;
    }
    setSiteLanguage(lang);
    document.documentElement.lang = lang;
    void i18n.changeLanguage(lang);
  }

  return (
    <Cluster gap={2} wrap={false}>
      <Button
        disabled={!hydrated}
        variant="ghost"
        size="small"
        icon={<RuFlagIcon />}
        aria-label={t('footer.switchToRu')}
        aria-pressed={current === Lang.RU}
        testId={`${testId}-ru`}
        onClick={() => select(Lang.RU)}
      />
      <Button
        disabled={!hydrated}
        variant="ghost"
        size="small"
        icon={<UsFlagIcon />}
        aria-label={t('footer.switchToEn')}
        aria-pressed={current === Lang.EN}
        testId={`${testId}-en`}
        onClick={() => select(Lang.EN)}
      />
    </Cluster>
  );
}
